import React from 'react'
import { Users, TrendingUp, AlertTriangle, Calendar, Target, Award, Clock, Zap, CheckCircle, AlertCircle } from 'lucide-react'

interface TeamStats {
  totalMembers: number
  activeMembers: number
  averageScore: number
  teamTrend: 'up' | 'down' | 'neutral'
  distributionByLevel: {
    [key: string]: number
  }
  completedOneOnOnes: number
  pendingActions: number
  upcomingReviews: number
}

interface TeamOverviewWidgetProps {
  stats: TeamStats
  teamName?: string
  period: string
}

const TeamOverviewWidget = ({ stats, teamName = "Equipe", period }: TeamOverviewWidgetProps) => {
  const performanceLevels = [
    { key: 'high', label: 'Alta Performance', color: 'bg-success', textColor: 'text-success', icon: Award },
    { key: 'potential', label: 'Potencial', color: 'bg-brand-primary', textColor: 'text-brand-primary', icon: Zap },
    { key: 'growth', label: 'Crescimento', color: 'bg-brand-blue', textColor: 'text-brand-blue', icon: TrendingUp },
    { key: 'fulfills', label: 'Cumpre Função', color: 'bg-info', textColor: 'text-info', icon: CheckCircle },
    { key: 'analysis', label: 'Sob Análise', color: 'bg-warning', textColor: 'text-warning', icon: Clock },
    { key: 'misaligned', label: 'Desalinhado', color: 'bg-error', textColor: 'text-error', icon: AlertCircle }
  ]

  const getTrendIcon = () => {
    switch (stats.teamTrend) {
      case 'up': return TrendingUp
      case 'down': return TrendingUp // Will be rotated with CSS
      default: return Target
    }
  }

  const getTrendColor = () => {
    switch (stats.teamTrend) {
      case 'up': return 'text-success'
      case 'down': return 'text-error'
      default: return 'text-brand-gray'
    }
  }

  const TrendIcon = getTrendIcon()

  return (
    <div className="bg-brand-dark-light rounded-monumenta p-8 border border-brand-gray/10 hover:border-brand-primary/30 transition-all duration-200 hover:shadow-monumenta-brand">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-display font-bold text-brand-primary">
            {teamName}
          </h2>
          <p className="text-brand-gray">Período: {period}</p>
        </div>
        <div className="p-3 bg-brand-primary/10 rounded-monumenta">
          <Users className="text-brand-primary" size={32} />
        </div>
      </div>

      {/* Key Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="text-center">
          <div className="text-3xl font-display font-bold text-white mb-1">
            {stats.totalMembers}
          </div>
          <div className="text-sm text-brand-gray">Total Membros</div>
          <div className="text-xs text-success mt-1">
            {stats.activeMembers} ativos
          </div>
        </div>

        <div className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-1">
            <span className="text-3xl font-display font-bold text-brand-primary">
              {stats.averageScore}
            </span>
            <TrendIcon 
              className={`${getTrendColor()} ${stats.teamTrend === 'down' ? 'rotate-180' : ''}`} 
              size={20} 
            />
          </div>
          <div className="text-sm text-brand-gray">Score Médio</div>
        </div>

        <div className="text-center">
          <div className="text-3xl font-display font-bold text-brand-blue mb-1">
            {stats.completedOneOnOnes}
          </div>
          <div className="text-sm text-brand-gray">1:1s Completos</div>
        </div>

        <div className="text-center">
          <div className="text-3xl font-display font-bold text-warning mb-1">
            {stats.pendingActions}
          </div>
          <div className="text-sm text-brand-gray">Ações Pendentes</div>
        </div>
      </div>

      {/* Performance Distribution */}
      <div className="mb-8">
        <h3 className="text-lg font-display font-semibold text-white mb-4">
          Distribuição de Performance
        </h3>
        <div className="space-y-3">
          {performanceLevels.map((level, index) => {
            const count = stats.distributionByLevel[level.key] || 0
            const percentage = stats.totalMembers > 0 ? (count / stats.totalMembers) * 100 : 0
            
            return (
              <div key={index} className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 min-w-[120px]">
                  <level.icon className={`${level.textColor} flex-shrink-0`} size={16} />
                  <span className="text-sm text-brand-gray">{level.label}</span>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <div className="w-full bg-brand-dark rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-500 ${level.color}`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                
                <div className="text-sm font-display font-medium text-white min-w-[40px] text-right">
                  {count}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Action Items */}
      <div className="border-t border-brand-gray/20 pt-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-brand-dark/30 rounded-monumenta p-4 text-center">
            <Calendar className="text-brand-blue mx-auto mb-2" size={24} />
            <div className="text-lg font-display font-bold text-brand-blue">
              {stats.upcomingReviews}
            </div>
            <div className="text-xs text-brand-gray">
              Avaliações próximas
            </div>
          </div>

          <div className="bg-brand-dark/30 rounded-monumenta p-4 text-center">
            <Clock className="text-warning mx-auto mb-2" size={24} />
            <div className="text-lg font-display font-bold text-warning">
              {Math.round((stats.completedOneOnOnes / stats.activeMembers) * 100)}%
            </div>
            <div className="text-xs text-brand-gray">
              Taxa 1:1 mensal
            </div>
          </div>

          <div className="bg-brand-dark/30 rounded-monumenta p-4 text-center">
            <Award className="text-brand-primary mx-auto mb-2" size={24} />
            <div className="text-lg font-display font-bold text-brand-primary">
              {stats.distributionByLevel.high || 0}
            </div>
            <div className="text-xs text-brand-gray">
              Alta performance
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TeamOverviewWidget
